import React from 'react'
import { 
  Sidebar, 
  SidebarContent,
  cn
} from '@merels/ui'
import { useSignals } from '@preact/signals-react/runtime'
import { useFocusNavigationDep } from '../../providers/FocusNavigationProvider/FocusNavigationProvider'
import { useWebContainer } from '../../providers/DepProvider/DepProvider'
import { useListNavigation } from '../../../hooks/useListNavigation'
import { MoveHistory } from '../game/MoveHistory/MoveHistory'
import { DeploymentInfo } from '../deployment/DeploymentInfo'
import { PublishingInfo } from '../deployment/PublishingInfo/PublishingInfo'

import { GamePublishInfo } from '../deployment/GamePublishInfo'
import { TestStateSelector } from '../game/TestStateSelector/TestStateSelector'
import { StateEditor } from '../game/StateEditor/StateEditor'
import { ScreenshotDebug } from '../game/ScreenshotDebug/ScreenshotDebug'
import { ColorScheme } from '../colors/ColorScheme/ColorScheme'
import { ControllerDebug } from '../controllers/ControllerDebug/ControllerDebug'
import { GamepadEmulationDebug } from '../controllers/GamepadEmulationDebug'
import { RoomDebug } from '../room/RoomDebug'
import { DevelopmentStatusCard } from '../../development/DevelopmentStatus/DevelopmentStatus'
import { ToolSubtabs } from '../navigation/ToolSubtabs/ToolSubtabs'
import type { MoveHistoryUI } from '../../../deps/ui/debugPanelVM/moveHistoryUI/moveHistoryUI.types'
import type { StateEditor as StateEditorDep } from '../../../deps/debug/stateEditor/stateEditor.types'
import type { GamepadEmulation } from '../../../deps/debug/gamepadEmulation/gamepadEmulation.types'
import type { DebugPanelVM } from '../../../deps/ui/debugPanelVM/debugPanelVM.types'
import {
  LucideIcon,
  Bug,
  Package,
  ChessKnight,
  Braces,
  Ticket,
  Info,
  Palette,
  Gamepad2,
  Wrench,
  Sofa,
  ListChecks,
  Microscope,
  Terminal,
  Search,
  X,
  Layers,
  LayoutFreeform,
} from 'lucide-react'

interface DebugPanelTabsProps {
  debugPanelVM: DebugPanelVM
  moveHistoryUI: MoveHistoryUI
  stateEditor: StateEditorDep
  gamepadEmulation: GamepadEmulation
}

interface ToolDef {
  id: string
  name: string
  Icon: LucideIcon
  description: string
  requiresActiveGame?: boolean
  requiresOwnedGame?: boolean
  wrapperClassName?: string
  category?: 'playtesting' | 'debugging' | 'platform' | 'other'
}

const CREATOR_PRIMARY_TOOLS = ['presets', 'status', 'creator_checklist', 'issues', 'game_builds', 'emulation']
const DEBUG_PRIMARY_TOOLS = ['issues', 'game_builds', 'presets', 'emulation', 'publishing']
const DEBUGGING_TOOLS = ['moves', 'state', 'colors']
const PLATFORM_TOOLS = ['app_info', 'debugger', 'room_debugger']

export function DebugPanelTabs({
  debugPanelVM,
  moveHistoryUI,
  stateEditor,
  gamepadEmulation,
}: DebugPanelTabsProps) {
  useSignals()
  const { roomActions } = useWebContainer()

  const error = debugPanelVM.errorText.value
  const moveHistory = debugPanelVM.moveHistoryEntries.value
  const ourMateID = debugPanelVM.ourMateId.value
  const mates = debugPanelVM.mates.value
  const encodeMove = debugPanelVM.encodeMove.value
  const moveJson = debugPanelVM.moveJson.value
  const onMoveJsonChange = debugPanelVM.setMoveJson
  const onSubmitMove = debugPanelVM.submitMove
  const aiProgressMessages = debugPanelVM.aiProgressMessages.value
  const deploymentInfo = debugPanelVM.deploymentInfo.value
  const gameId = debugPanelVM.gameId.value
  const positions = debugPanelVM.positions?.value || debugPanelVM.testStates.value
  const initialStateName = debugPanelVM.initialStateName.value || undefined
  const onApplyState = debugPanelVM.applyState
  const currentGameState = debugPanelVM.currentGameState.value
  const currentCtx = debugPanelVM.currentCtx.value
  const isLocalMode = debugPanelVM.isLocalMode.value
  const isOmniMode = debugPanelVM.isOmniMode.value
  const matesDebugData = debugPanelVM.matesDebugData.value
  const onFlushMates = debugPanelVM.flushMates
  const gamePublishChannel = debugPanelVM.gamePublishChannel.value
  const gameBuildInfo = debugPanelVM.gameBuildInfo.value
  const isGameDynamicallyLoaded = debugPanelVM.isGameDynamicallyLoaded.value
  const gameChannelPreference = debugPanelVM.gameChannelPreference.value
  const availableBuilds = debugPanelVM.availableBuilds.value
  const expandedBuildMessageIds = debugPanelVM.expandedBuildMessageIds.value
  const isOwnedGame = debugPanelVM.isOwnedGame?.value ?? false
  const isPlatformDeveloper = debugPanelVM.isPlatformDeveloper?.value ?? false
  const developmentProgress = debugPanelVM.developmentProgress?.value ?? null

  const hasActiveGame = !!gameId
  const noActiveGame = <div className="h-full overflow-auto p-4 text-sm text-muted text-center">No active game</div>

  // Generic detail view item tracking
  const [activeDetailItem, setActiveDetailItem] = React.useState<any | null>(null)
  const [copiedLink, setCopiedLink] = React.useState(false)
  const [onCopyLink, setOnCopyLink] = React.useState<(() => void) | null>(null)

  const activeIconRef = React.useRef<HTMLButtonElement | null>(null)

  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (activeIconRef.current) {
        activeIconRef.current.focus()
      } else {
        const fallback = document.querySelector('[data-tool-btn]') as HTMLButtonElement | null
        fallback?.focus()
      }
    }, 150)
    return () => clearTimeout(timer)
  }, [])

  const handleIconKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (isFocusNavActive) return // handoff to useListNavigation

    const container = e.currentTarget.parentElement
    if (!container) return

    const btns = Array.from(
      container.querySelectorAll('[data-tool-btn]')
    ) as HTMLButtonElement[]

    if (btns.length === 0) return
    const idx = btns.indexOf(e.currentTarget)

    if (e.key === 'ArrowRight') {
      e.preventDefault()
      const nextIdx = (idx + 1) % btns.length
      btns[nextIdx].focus()
      const nextToolId = btns[nextIdx].getAttribute('data-tool-btn')
      if (nextToolId) {
        selectTopLevelTool(nextToolId)
      }
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault()
      const prevIdx = (idx - 1 + btns.length) % btns.length
      btns[prevIdx].focus()
      const prevToolId = btns[prevIdx].getAttribute('data-tool-btn')
      if (prevToolId) {
        selectTopLevelTool(prevToolId)
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      const contentArea = document.querySelector('[data-testid="tool-content-area"]')
      if (contentArea) {
        const focusable = contentArea.querySelector(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [data-list-item="true"], [tabindex="0"]'
        ) as HTMLElement | null
        focusable?.focus()
      }
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      e.currentTarget.click()
    }
  }

  const handleCopyLink = () => {
    if (!onCopyLink) return
    onCopyLink()
    setCopiedLink(true)
    setTimeout(() => setCopiedLink(false), 2000)
  }


  // Creator-facing tools are primary; advanced tiers open through section entries.
  const tools: ToolDef[] = [
    {
      id: 'status',
      name: 'Development Status',
      Icon: Layers,
      description: 'Track game readiness through Draft, Alpha, Beta, and Released stages, current step, and remaining work to Beta.',
      requiresActiveGame: true,
      requiresOwnedGame: true,
      category: 'playtesting',
    },
    {
      id: 'creator_checklist',
      name: 'Creator Checklist',
      Icon: ListChecks,
      description: 'Track your progress through game creation.',
      category: 'playtesting',
    },
    {
      id: 'issues',
      name: 'Issues',
      Icon: Bug,
      description: 'Report a problem with a short description and a cropped screenshot so the agent can investigate the game state you saw.',
      requiresActiveGame: true,
      category: 'playtesting',
    },
    {
      id: 'game_builds',
      name: 'Game Builds',
      Icon: Package,
      description: 'Check which build is running, refresh available builds, or switch the build you are playtesting.',
      category: 'playtesting',
    },
    {
      id: 'presets',
      name: 'Positions',
      Icon: LayoutFreeform,
      description: 'Load a prepared game position to playtest a specific moment without reaching it through normal play.',
      requiresActiveGame: true,
      category: 'playtesting',
    },
    {
      id: 'moves',
      name: 'Moves',
      Icon: ChessKnight,
      description: 'Review move history and agent progress, or submit an encoded move while investigating game logic.',
      requiresActiveGame: true,
      category: 'debugging',
    },
    {
      id: 'state',
      name: 'State',
      Icon: Braces,
      description: 'Inspect or edit the current game state and context when reproducing or recovering from a problem.',
      requiresActiveGame: true,
      category: 'debugging',
    },
    {
      id: 'publishing',
      name: 'Publishing',
      Icon: Ticket,
      description: 'Manage the publishers, creators, and developer tokens used to publish games.',
      category: 'other',
    },

    {
      id: 'app_info',
      name: 'App Info',
      Icon: Info,
      description: 'Check the app build, commit, and worker address to confirm which environment is running.',
      category: 'platform',
    },
    {
      id: 'colors',
      name: 'Colors',
      Icon: Palette,
      description: 'Inspect active theme and player colors when checking contrast or unexpected styling.',
      category: 'debugging',
    },
    {
      id: 'emulation',
      name: 'Gamepad',
      Icon: Gamepad2,
      description: 'Use virtual gamepads to playtest local controls and participation without physical controllers.',
      category: 'playtesting',
    },
    {
      id: 'debugger',
      name: 'Controller Debugger',
      Icon: Wrench,
      description: 'Watch live buttons, axes, and connection data from physical gamepads to diagnose input problems.',
      category: 'platform',
    },
    {
      id: 'room_debugger',
      name: 'Room Debugger',
      Icon: Sofa,
      description: 'Inspect room, mate, player-assignment, and connection data when diagnosing a broken room.',
      wrapperClassName: 'h-full flex flex-col',
      category: 'platform',
    },
    {
      id: 'debugging_section',
      name: 'Debugging',
      Icon: Microscope,
      description: 'Inspect moves, state, and colors when playtesting reveals unexpected behavior.',
      category: 'playtesting',
    },
    {
      id: 'platform_section',
      name: 'Platform',
      Icon: Terminal,
      description: 'Inspect app, controller, and room diagnostics used for platform development.',
      category: 'platform',
    },
  ]

  // Persisted state loading
  const [activeToolId, setActiveToolId] = React.useState<string>(() => {
    if (typeof window === 'undefined') return isOwnedGame ? 'presets' : 'issues'
    const suffix = ourMateID || 'default'
    return localStorage.getItem(`merels_active_debug_tool_${suffix}`) || (isOwnedGame ? 'presets' : 'issues')
  })
  const [searchQuery, setSearchQuery] = React.useState('')
  const [isSearchOpen, setIsSearchOpen] = React.useState(false)
  const [showToolHelp, setShowToolHelp] = React.useState(false)
  const [activeIndex, setActiveIndex] = React.useState<number>(-1)


  const setActiveTool = React.useCallback((toolId: string) => {
    setActiveToolId(toolId)
    setActiveDetailItem(null)
    setOnCopyLink(null)
    const suffix = ourMateID || 'default'
    localStorage.setItem(`merels_active_debug_tool_${suffix}`, toolId)
  }, [ourMateID])

  const selectTool = React.useCallback((toolId: string) => {
    setActiveTool(toolId)
  }, [ourMateID, setActiveTool])

  React.useEffect(() => {
    setActiveIndex(-1)
  }, [searchQuery])

  const focusNavigation = useFocusNavigationDep()
  const isFocusNavActive = focusNavigation.isActive.value
  const isOpen = debugPanelVM.isOpen.value

  const availableTools = tools.filter((tool) => tool.category !== 'platform' || isPlatformDeveloper)

  const primaryToolIds = isOwnedGame ? CREATOR_PRIMARY_TOOLS : DEBUG_PRIMARY_TOOLS
  const topLevelToolIds = [
    ...primaryToolIds,
    'debugging_section',
    ...(isPlatformDeveloper ? ['platform_section'] : []),
  ]
  const renderedTools = topLevelToolIds
    .map((id) => availableTools.find((tool) => tool.id === id))
    .filter((tool): tool is ToolDef => !!tool)
  const activeTool = availableTools.find((tool) => tool.id === activeToolId) || availableTools[0]

  const selectTopLevelTool = React.useCallback((toolId: string) => {
    selectTool(
      toolId === 'debugging_section'
        ? DEBUGGING_TOOLS[0]
        : toolId === 'platform_section'
          ? PLATFORM_TOOLS[0]
          : toolId,
    )
  }, [selectTool])

  const isTopLevelToolActive = React.useCallback((toolId: string) => {
    if (toolId === 'debugging_section') return activeTool.category === 'debugging'
    if (toolId === 'platform_section') return activeTool.category === 'platform'
    return activeTool.id === toolId
  }, [activeTool])

  React.useEffect(() => {
    if (!availableTools.some((tool) => tool.id === activeToolId)) {
      setActiveToolId(isOwnedGame ? 'creator_checklist' : 'issues')
    }
  }, [activeToolId, availableTools, isOwnedGame])

  const initialIndex = React.useMemo(() => {
    const idx = renderedTools.findIndex((tool) => isTopLevelToolActive(tool.id))
    return idx === -1 ? 0 : idx
  }, [renderedTools, isTopLevelToolActive])

  const tabsNav = useListNavigation(
    renderedTools,
    React.useCallback((tool) => {
      selectTopLevelTool(tool.id)
    }, [selectTopLevelTool]),
    isOpen && isFocusNavActive && focusNavigation.focusArea.value === 'sidebar',
    React.useCallback((index: number) => {
      const tool = renderedTools[index]
      if (tool) {
        selectTopLevelTool(tool.id)
      }
    }, [renderedTools, selectTopLevelTool]),
    false,
    React.useMemo(() => ({
      initialIndex,
      computeNextIndex: (current: number, direction: 'up' | 'down' | 'left' | 'right', itemsLength: number) => {
        if (direction === 'down') {
          const contentArea = document.querySelector('[data-testid="tool-content-area"]')
          if (contentArea) {
            setTimeout(() => {
              const focusable = contentArea.querySelector(
                'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [data-list-item="true"], [tabindex="0"]'
              ) as HTMLElement | null
              focusable?.focus()
            }, 0)
          }
          return current
        }
        if (direction === 'left' || direction === 'up') {
          return current > 0 ? current - 1 : itemsLength - 1
        }
        if (direction === 'right') {
          return current < itemsLength - 1 ? current + 1 : 0
        }
        return current
      }
    }), [initialIndex])
  )

  React.useEffect(() => {
    if (isOpen) {
      focusNavigation.setFocusArea('sidebar')
    } else {
      if (focusNavigation.focusArea.value === 'sidebar') {
        focusNavigation.setFocusArea('content')
      }
    }
    return () => {
      if (focusNavigation.focusArea.value === 'sidebar') {
        focusNavigation.setFocusArea('content')
      }
    }
  }, [isOpen, focusNavigation])

  const triggerCapture = debugPanelVM.triggerCaptureScreenshot.value
  React.useEffect(() => {
    if (triggerCapture) {
      setActiveTool('issues')
    }
  }, [triggerCapture, setActiveTool])

  const requestedTool = debugPanelVM.requestedToolId?.value
  React.useEffect(() => {
    if (requestedTool) {
      selectTool(requestedTool)
      debugPanelVM.clearRequestedTool?.()
    }
  }, [requestedTool, selectTool, debugPanelVM])

  React.useEffect(() => {
    const suffix = ourMateID || 'default'
    const activeKey = `merels_active_debug_tool_${suffix}`

    const savedActive = localStorage.getItem(activeKey)

    if (savedActive) {
      setActiveToolId(savedActive)
    } else {
      // A mate ID can settle after the panel is already interactive. Preserve the
      // tool selected during that transition instead of snapping back to a default.
      setActiveToolId((current) => {
        localStorage.setItem(activeKey, current)
        return current
      })
    }
  }, [ourMateID, isOwnedGame])



  // Autocomplete filtering logic
  const searchableTools = availableTools.filter(
    (tool) => tool.id !== 'debugging_section' && tool.id !== 'platform_section',
  )
  const filteredTools = React.useMemo(() => {
    if (!searchQuery) return searchableTools
    const q = searchQuery.toLowerCase()
    return searchableTools.filter(
      (tool) => tool.name.toLowerCase().includes(q)
        || tool.id.toLowerCase().includes(q)
        || tool.category?.includes(q),
    )
  }, [searchQuery, searchableTools])

  const handleSelectSearch = (toolId: string) => {
    selectTool(toolId)
    setSearchQuery('')
    setIsSearchOpen(false)
    setActiveIndex(-1)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isSearchOpen || filteredTools.length === 0) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((prev) => (prev + 1) % filteredTools.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((prev) => (prev - 1 + filteredTools.length) % filteredTools.length)
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && activeIndex < filteredTools.length) {
        e.preventDefault()
        handleSelectSearch(filteredTools[activeIndex].id)
      }
    } else if (e.key === 'Escape') {
      setIsSearchOpen(false)
      setActiveIndex(-1)
    }
  }

  const activeCategory = activeTool.category
  const debuggingTools = DEBUGGING_TOOLS
    .map((id) => availableTools.find((tool) => tool.id === id))
    .filter((tool): tool is ToolDef => !!tool)
  const platformTools = PLATFORM_TOOLS
    .map((id) => availableTools.find((tool) => tool.id === id))
    .filter((tool): tool is ToolDef => !!tool)

  // Get active tool render configuration
  const requiresGame = activeTool.requiresActiveGame && !hasActiveGame

  const leaf = React.useMemo(() => {
    let title = activeTool.name
    if (activeDetailItem) {
      if (activeTool.id === 'issues') {
        title = `Issue #${activeDetailItem.issueId || activeDetailItem.issue_id}`
      } else if (activeTool.id === 'tokens') {
        title = activeDetailItem.displayName
      }
    }

    return {
      title,
      wrapperClassName: activeTool.wrapperClassName || 'h-full overflow-auto',
      actions: activeTool.id === 'issues' && activeDetailItem ? (
        <button
          onClick={handleCopyLink}
          className="text-accent hover:opacity-85 transition-all flex items-center gap-1.5"
          title="Copy Link"
        >
          {copiedLink ? (
            <span className="text-[10px] font-semibold text-accent animate-pulse">✓ Link Copied</span>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
              <polyline points="16 6 12 2 8 6" />
              <line x1="12" x2="12" y1="2" y2="15" />
            </svg>
          )}
        </button>
      ) : null
    }
  }, [activeTool, activeDetailItem, copiedLink, handleCopyLink])

  return (
    <Sidebar
      style={{
        '--debug-sidebar-width': '320px',
        '--debug-sidebar-bg': 'hsl(var(--background))'
      } as React.CSSProperties}
      className="border-l border-basic w-[var(--debug-sidebar-width)] min-w-[var(--debug-sidebar-width)] max-w-[var(--debug-sidebar-width)] flex-shrink-0 flex flex-col h-full min-h-0 bg-[var(--debug-sidebar-bg)]"
    >
      <SidebarContent className="flex flex-col flex-1 min-h-0 px-2 pt-0.5 pb-2 gap-2">
        {/* Persistent Title Header */}
        <div className="flex items-center justify-between px-1 pt-1 pb-0.5 flex-shrink-0 border-b border-basic/25">
          <span className="text-xs font-bold tracking-wider uppercase text-muted" data-testid="sidebar-title">
            {isOwnedGame ? 'Creator Panel' : 'Debug Panel'}
          </span>
        </div>

        {error && (
          <div className="bg-danger-bg/20 border-2 border-danger-bg/40 text-danger p-4 rounded-lg font-bold text-sm">
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Primary tools and category tabs */}
        <div className="flex flex-col gap-1.5 flex-shrink-0">
          <div className="flex items-center gap-1 select-none" role="tablist" aria-label="Creator Panel tools">
            <div className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto">
              {renderedTools.map((tool, index) => {
                const isActive = isTopLevelToolActive(tool.id)

                return (
                  <button
                    key={tool.id}
                    ref={(element) => {
                      tabsNav.registerItem(index, element)
                      if (isActive) activeIconRef.current = element
                    }}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    data-tool-btn={tool.id}
                    aria-label={tool.name}
                    onClick={() => selectTopLevelTool(tool.id)}
                    onKeyDown={handleIconKeyDown}
                    className={cn(
                      'group relative flex h-8 w-8 flex-shrink-0 items-center justify-center rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus',
                      isActive
                        ? 'bg-accent/15 text-accent'
                        : 'text-muted hover:bg-muted-bg/30 hover:text-primary',
                    )}
                    title={tool.name}
                  >
                    <tool.Icon className="h-4 w-4" strokeWidth={isActive ? 2.75 : 2} aria-hidden="true" />
                    <span className="pointer-events-none absolute left-1/2 top-9 z-50 -translate-x-1/2 whitespace-nowrap rounded border border-basic bg-surface px-1.5 py-0.5 text-[10px] font-semibold text-primary opacity-0 shadow transition-opacity group-hover:opacity-100">
                      {tool.name}
                    </span>
                  </button>
                )
              })}
            </div>

            <button
              type="button"
              aria-label={isSearchOpen ? 'Close tool search' : 'Search tools'}
              aria-expanded={isSearchOpen}
              onClick={() => {
                setIsSearchOpen((open) => !open)
                setActiveIndex(-1)
              }}
              className={cn(
                'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus',
                isSearchOpen
                  ? 'bg-accent/15 text-accent'
                  : 'text-muted hover:bg-muted-bg/30 hover:text-primary',
              )}
              title="Search tools"
            >
              {isSearchOpen ? <X className="h-4 w-4" /> : <Search className="h-4 w-4" />}
            </button>
          </div>

          {activeCategory === 'debugging' && (
            <ToolSubtabs tools={debuggingTools} activeToolId={activeToolId} onSelect={selectTool} />
          )}
          {activeCategory === 'platform' && isPlatformDeveloper && (
            <ToolSubtabs tools={platformTools} activeToolId={activeToolId} onSelect={selectTool} />
          )}

          {isSearchOpen && (
            <div className="relative rounded-md border border-basic bg-surface p-2 shadow-sm">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted" aria-hidden="true" />
                <input
                  autoFocus
                  type="search"
                  aria-label="Search all Creator Panel tools"
                  className="w-full rounded border border-basic bg-surface py-1.5 pl-7 pr-2 text-xs text-primary placeholder:text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                  placeholder="Search all tools..."
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </div>
              <div className="mt-2 max-h-48 space-y-1 overflow-y-auto" role="listbox" aria-label="Tool search results">
                {filteredTools.map((tool, index) => {
                  const isHighlighted = index === activeIndex
                  return (
                    <button
                      key={tool.id}
                      type="button"
                      role="option"
                      aria-selected={isHighlighted}
                      onClick={() => handleSelectSearch(tool.id)}
                      onMouseEnter={() => setActiveIndex(index)}
                      className={cn(
                        'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs transition-colors',
                        isHighlighted ? 'bg-accent/15 text-accent' : 'text-primary hover:bg-subtle',
                      )}
                    >
                      <tool.Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                      <span className="min-w-0 flex-1 truncate font-semibold">{tool.name}</span>
                      <span className="text-[10px] capitalize text-muted">{tool.category}</span>
                    </button>
                  )
                })}
                {filteredTools.length === 0 && (
                  <p className="px-2 py-3 text-center text-xs text-muted">No matching tools</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* 3. Uniform Leaf Header */}
        <div className="px-1 flex items-center justify-between text-base font-bold leading-none flex-shrink-0" data-testid="debug-sidebar-leaf-title">
          <div className="flex items-center gap-1.5">
            {activeDetailItem && (
              <button
                onClick={() => setActiveDetailItem(null)}
                className="text-accent hover:opacity-85 transition-all p-1 -ml-1 rounded hover:bg-muted-bg/30 flex items-center justify-center"
                title="Back to list"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m15 18-6-6 6-6" />
                </svg>
              </button>
            )}
            <span>{leaf.title}</span>
            {!activeDetailItem && (
              <button
                type="button"
                aria-label={showToolHelp ? 'Hide tools help' : 'Show tools help'}
                onClick={() => setShowToolHelp((show) => !show)}
                className={cn(
                  "p-1 rounded hover:bg-muted-bg/30 text-muted hover:text-accent transition-colors flex items-center justify-center",
                  showToolHelp && "text-accent bg-accent/10"
                )}
                title={showToolHelp ? 'Hide tools help' : 'Show tools help'}
              >
                <Info className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {leaf.actions}
        </div>

        {/* Collapsible Info Explanation Box */}
        {showToolHelp && (
          <div
            data-testid="tool-help"
            className="px-1 py-0.5 text-xs leading-snug text-muted animate-in fade-in slide-in-from-top-1 duration-200"
          >
            {activeTool.description}
          </div>
        )}

        {/* 4. Active Tool Content Area */}
        <div className="flex flex-col flex-1 min-h-0" data-testid="tool-content-area">
          <div className={leaf.wrapperClassName}>
            {requiresGame ? (
              noActiveGame
            ) : (
              <>
                {activeToolId === 'status' && (
                  developmentProgress ? (
                    <div className="flex-1 overflow-auto p-4">
                      <DevelopmentStatusCard progress={developmentProgress} />
                    </div>
                  ) : (
                    <div className="flex-1 overflow-auto p-4 text-sm text-muted text-center">
                      No development status available for {gameId || 'this game'}
                    </div>
                  )
                )}
                {activeToolId === 'issues' && (
                  <ScreenshotDebug
                    activeIssueProp={activeDetailItem}
                    onActiveIssueChange={(issue, copyFn) => {
                      setActiveDetailItem(issue)
                      setOnCopyLink(() => copyFn || null)
                    }}
                  />
                )}
                {activeToolId === 'creator_checklist' && (
                  developmentProgress ? (
                    <DevelopmentStatusCard progress={developmentProgress} />
                  ) : (
                    <div className="p-4 text-center text-sm text-muted">
                      No creator checklist is available for this game.
                    </div>
                  )
                )}
                {activeToolId === 'game_builds' && (
                  <GamePublishInfo
                    gameId={gameId}
                    channel={gamePublishChannel}
                    hasActiveGame={hasActiveGame}
                    buildInfo={gameBuildInfo}
                    isDynamicallyLoaded={isGameDynamicallyLoaded}
                    channelPreference={gameChannelPreference}
                    onSetChannelPreference={debugPanelVM.setGameChannelPreference}
                    availableBuilds={availableBuilds}
                    onSelectBuild={debugPanelVM.selectGameBuild}
                    onRefreshBuilds={debugPanelVM.refreshAvailableBuilds}
                    expandedBuildMessageIds={expandedBuildMessageIds}
                    onToggleBuildMessageExpanded={debugPanelVM.toggleBuildMessageExpanded}
                  />
                )}
                {activeToolId === 'presets' && (
                  gameId && positions.length > 0 ? (
                    <TestStateSelector
                      gameId={gameId}
                      availableStates={positions}
                      onApplyState={onApplyState}
                      initialStateName={initialStateName}
                    />
                  ) : (
                    <div className="flex-1 overflow-auto p-4 text-sm text-muted text-center">
                      No positions available for {gameId || 'this game'}
                    </div>
                  )
                )}
                {activeToolId === 'moves' && (
                  <MoveHistory
                    moveHistory={moveHistory}
                    ourMateID={ourMateID}
                    mates={mates.map((m) => ({ id: m.mateId, isAI: m.isAI }))}
                    encodeMove={encodeMove}
                    moveJson={moveJson}
                    onMoveJsonChange={onMoveJsonChange}
                    onSubmitMove={onSubmitMove}
                    aiProgressMessages={aiProgressMessages}
                    moveHistoryUI={moveHistoryUI}
                    isLocalMode={isLocalMode}
                    isOmniMode={isOmniMode}
                  />
                )}
                {activeToolId === 'state' && (
                  <StateEditor
                    gameState={currentGameState}
                    ctx={currentCtx}
                    onApplyState={onApplyState}
                    stateEditor={stateEditor}
                  />
                )}
                {activeToolId === 'publishing' && <PublishingInfo />}

                {activeToolId === 'app_info' && <DeploymentInfo deploymentInfo={deploymentInfo} />}
                {activeToolId === 'colors' && <ColorScheme />}
                {activeToolId === 'emulation' && <GamepadEmulationDebug gamepadEmulation={gamepadEmulation} />}
                {activeToolId === 'debugger' && <ControllerDebug />}
                {activeToolId === 'room_debugger' && (
                  <RoomDebug
                    data={matesDebugData}
                    onFlushMates={onFlushMates}
                    onEndGame={() => void roomActions.endGame()}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </SidebarContent>
    </Sidebar>
  )
}
